const sortPointsClockwise = (overlap, maskList) => {
    const points = overlap.overlapPoints;
    if (points.length < 3) return points;
    if (overlap.mask1Type === "polygon" || overlap.mask2Type === "polygon") {
        // console.log("calling sortPolygonPoints", points);
        const polyPoints = sortPolygonPoints(points, maskList);
        if (polyPoints.length > 0) return polyPoints;
    }

    const centroid = points.reduce(
        (acc, p) => ({ x: acc.x + p.x / points.length, y: acc.y + p.y / points.length }),
        { x: 0, y: 0 }
    );

    const uniquePoints = Array.from(
        new Map(points.map(p => [`${p.x.toFixed(6)},${p.y.toFixed(6)}`, p])).values()
    );

    return uniquePoints.sort((a, b) => {
        const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
        const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
        return angleA - angleB;
    });
};

const sortPolygonPoints = (points, maskList) => {
    if (points.length < 3) return points;

    const round = (num) => Number(num.toFixed(4));
    const format = (point) => `${round(point.x)},${round(point.y)}`;

    const overlapSet = new Set(points.map(format));

    let polygonMask = null;
    let maxMatches = 0;

    for (const mask of maskList) {
        if (mask.type !== "polygon") continue;
        if (!Array.isArray(mask.points)) continue;

        const matchCount = mask.points.reduce((count, pt) =>
            overlapSet.has(format(pt)) ? count + 1 : count
            , 0);

        if (matchCount > maxMatches) {
            polygonMask = mask;
            maxMatches = matchCount;
        }
    }

    if (!polygonMask) {
        // console.log("PolygonMask Sorting based on centroid")
        return []
    }


    // console.log("PolygonMask found", polygonMask, points);

    return fitPointsIntoPolygon(polygonMask.points, points);
};

function fitPointsIntoPolygon(polygonPoints, unorderedPoints) {
    const remainingPoints = [...unorderedPoints];
    let orderedPoints = [];

    const isPointOnSegment = (p, a, b, tolerance = 2) => {
        const cross = Math.abs((b.y - a.y) * (p.x - a.x) - (b.x - a.x) * (p.y - a.y));
        if (cross > tolerance) return false;

        const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
        if (dot < 0) return false;

        const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
        if (dot > lenSq) return false;

        return true;
    };

    for (let i = 0; i < polygonPoints.length - 1; i++) {
        const a = polygonPoints[i];
        const b = polygonPoints[i + 1];

        const onSegment = [];

        for (let j = remainingPoints.length - 1; j >= 0; j--) {
            const p = remainingPoints[j];
            if (isPointOnSegment(p, a, b)) {
                onSegment.push(p);
                remainingPoints.splice(j, 1);
            }
        }

        onSegment.sort((p1, p2) => {
            const d1 = Math.hypot(p1.x - a.x, p1.y - a.y);
            const d2 = Math.hypot(p2.x - a.x, p2.y - a.y);
            return d1 - d2;
        });

        orderedPoints.push(...onSegment);
    }

    remainingPoints.sort((a, b) => {
        if (a.x < b.x) return -1;
        if (a.x > b.x) return 1;

        if (a.y < b.y) return -1;
        if (a.y > b.y) return 1;

        return 0;
    });

    orderedPoints = insertRemainingPoints(orderedPoints, remainingPoints);

    orderedPoints.push(orderedPoints[0]);
    return orderedPoints;
}

function insertRemainingPoints(orderedPoints, remainingPoints) {
    if (remainingPoints.length === 0) {
        return orderedPoints;
    }

    const pointToInsert = remainingPoints[0];

    const tolerance = 0.001;

    function compareWithTolerance(val1, val2) {
        return Math.abs(val1 - val2) < tolerance;
    }

    if (orderedPoints.length > 0) {
        const checkXMatchIndex = orderedPoints.findIndex(point => compareWithTolerance(point.x, pointToInsert.x));
        const checkYMatchIndex = orderedPoints.findIndex(point => compareWithTolerance(point.y, pointToInsert.y));

        // console.log("checkXMatchIndex", checkXMatchIndex, "checkYMatchIndex", checkYMatchIndex, pointToInsert);

        if (checkXMatchIndex !== -1) {
            // console.log("Matching x point found at index:", checkXMatchIndex);

            const prevY = orderedPoints[checkXMatchIndex - 1];
            const nextY = orderedPoints[checkXMatchIndex + 1];

            if (prevY !== undefined && nextY !== undefined) {
                // console.log("Both previous and next Y points found:", prevY, nextY);

                if (prevY.y === pointToInsert.y) {
                    // console.log("Previous point matches Y. Insert before current index.");
                    orderedPoints.splice(checkXMatchIndex, 0, pointToInsert);
                } else {
                    // console.log("Previous point does not match Y. Insert after current index.");
                    orderedPoints.splice(checkXMatchIndex + 1, 0, pointToInsert);
                }

            } else if (prevY !== undefined) {
                // console.log("Only previous Y point found:", prevY);

                if (prevY.y === pointToInsert.y) {
                    // console.log("Previous point matches Y. Insert before current index.");
                    orderedPoints.splice(checkXMatchIndex, 0, pointToInsert);
                } else {
                    // console.log("Previous point does not match Y. Insert after current index.");
                    orderedPoints.splice(checkXMatchIndex + 1, 0, pointToInsert);
                }

            } else if (nextY !== undefined) {
                // console.log("Only next Y point found:", nextY);

                if (nextY.y === pointToInsert.y) {
                    // console.log("Next point matches Y. Insert after current index.");
                    orderedPoints.splice(checkXMatchIndex + 1, 0, pointToInsert);
                } else {
                    // console.log("Next point does not match Y. Insert before current index.");
                    orderedPoints.splice(checkXMatchIndex, 0, pointToInsert);
                }

            } else {
                // console.log("No surrounding Y points found. Inserting at current index by default.");
                orderedPoints.splice(checkXMatchIndex, 0, pointToInsert);
            }



        } else if (checkYMatchIndex !== -1) {
            // console.log("Matching y point found at index:", checkYMatchIndex);

            const prevX = orderedPoints[checkYMatchIndex - 1];
            const nextX = orderedPoints[checkYMatchIndex + 1];

            if (prevX !== undefined && nextX !== undefined) {
                // console.log("Both previous and next X points found:", prevX, nextX);

                if (prevX.x === pointToInsert.x) {
                    // console.log("Previous point matches X. Insert before current index.");
                    orderedPoints.splice(checkYMatchIndex, 0, pointToInsert);
                } else {
                    // console.log("Previous point does not match X. Insert after current index.");
                    orderedPoints.splice(checkYMatchIndex + 1, 0, pointToInsert);
                }

            } else if (prevX !== undefined) {
                // console.log("Only previous X point found:", prevX);
                if (prevX.x === pointToInsert.x) {
                    // console.log("Previous point matches X. Insert before current index.");
                    orderedPoints.splice(checkYMatchIndex, 0, pointToInsert);
                } else {
                    // console.log("Previous point does not match X. Insert after current index.");
                    orderedPoints.splice(checkYMatchIndex + 1, 0, pointToInsert);
                }

            } else if (nextX !== undefined) {
                // console.log("Only next X point found:", nextX);
                if (nextX.x === pointToInsert.x) {
                    // console.log("Next point matches X. Insert after current index.");
                    orderedPoints.splice(checkYMatchIndex + 1, 0, pointToInsert);
                } else {
                    // console.log("Next point does not match X. Insert before current index.");
                    orderedPoints.splice(checkYMatchIndex, 0, pointToInsert);
                }

            } else {
                // console.log("No surrounding X points found. Inserting at current index by default.");
                orderedPoints.splice(checkYMatchIndex, 0, pointToInsert);
            }
        } else {
            orderedPoints.push(pointToInsert);
            // console.log("Inserted point at the end (no x or y match):", orderedPoints[orderedPoints.length - 1]);
        }
    }

    return insertRemainingPoints(orderedPoints, remainingPoints.slice(1));
}













export { sortPointsClockwise };